import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Search, Edit2, Trash2, Loader2, Package, AlertTriangle, Sparkles,
  Eye, EyeOff, Boxes, Wallet, TrendingUp, PackageX
} from 'lucide-react';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

const EMPTY_FORM = {
  name: '', barcode: '', category: '', buy_price: '', sell_price: '',
  stock: '', unit: 'adet', min_stock: '', description: '',
  supplier: '', brand: '', image_url: '', origin: ''
};

const money = (n) => (n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 }) + ' TL';

export default function HesapProductsPage() {
  const { token } = useHesapAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suggestions, setSuggestions] = useState({ brands: [], suppliers: [], categories: [] });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [catForm, setCatForm] = useState('');
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [hidePrices, setHidePrices] = useState(() => localStorage.getItem('products-hide-prices') === '1');

  // Barkod
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupSuggestion, setLookupSuggestion] = useState(null);
  const [duplicateProduct, setDuplicateProduct] = useState(null); // Aynı barkod zaten var mı?
  const [autoLookupTriedFor, setAutoLookupTriedFor] = useState('');
  const debounceRef = useRef(null);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    loadProducts();
    loadCategories();
    loadSuggestions();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { localStorage.setItem('products-hide-prices', hidePrices ? '1' : '0'); }, [hidePrices]);

  const loadProducts = async () => {
    try {
      const res = await fetch(`${API}/products`, { headers });
      if (res.ok) setProducts(await res.json());
    } finally { setLoading(false); }
  };
  const loadCategories = async () => {
    const res = await fetch(`${API}/categories`, { headers });
    if (res.ok) setCategories((await res.json()).filter(c => c.type === 'product'));
  };
  const loadSuggestions = async () => {
    const res = await fetch(`${API}/products/suggestions`, { headers });
    if (res.ok) setSuggestions(await res.json());
  };
  const loadStats = async () => {
    const res = await fetch(`${API}/products/stats`, { headers });
    if (res.ok) setStats(await res.json());
  };

  const openDialog = (product = null) => {
    setLookupSuggestion(null);
    setDuplicateProduct(null);
    setAutoLookupTriedFor('');
    if (product) {
      setEditing(product);
      setForm({
        name: product.name, barcode: product.barcode || '', category: product.category || '',
        buy_price: product.buy_price || '', sell_price: product.sell_price || '',
        stock: product.stock || '', unit: product.unit || 'adet',
        min_stock: product.min_stock || '', description: product.description || '',
        supplier: product.supplier || '', brand: product.brand || '',
        image_url: product.image_url || '', origin: product.origin || ''
      });
    } else {
      setEditing(null);
      setForm(EMPTY_FORM);
    }
    setShowDialog(true);
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Ürün adı gerekli'); return; }
    // Yeni ürün eklerken duplicate barkod kontrolü
    if (!editing && duplicateProduct) {
      const ok = window.confirm(`"${duplicateProduct.name}" adında ürün bu barkodla zaten kayıtlı. Yine de yeni ürün olarak eklensin mi?`);
      if (!ok) return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        buy_price: parseFloat(form.buy_price) || 0,
        sell_price: parseFloat(form.sell_price) || 0,
        stock: parseInt(form.stock) || 0,
        min_stock: parseInt(form.min_stock) || 0
      };
      const url = editing ? `${API}/products/${editing.id}` : `${API}/products`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      toast.success(editing ? 'Ürün güncellendi' : 'Ürün eklendi');
      setShowDialog(false);
      loadProducts();
      loadSuggestions();
      loadStats();
    } catch { toast.error('Kayıt başarısız'); }
    finally { setSaving(false); }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`"${p.name}" silinsin mi?`)) return;
    await fetch(`${API}/products/${p.id}`, { method: 'DELETE', headers });
    toast.success('Ürün silindi');
    loadProducts();
    loadStats();
  };

  const addCategory = async () => {
    if (!catForm.trim()) return;
    await fetch(`${API}/categories`, { method: 'POST', headers, body: JSON.stringify({ name: catForm, type: 'product' }) });
    setCatForm('');
    setShowCatDialog(false);
    loadCategories();
    toast.success('Kategori eklendi');
  };

  // Barkod: duplicate check + Open Facts lookup
  const runBarcodeLookup = async (bc, { silent = false } = {}) => {
    const barcode = (bc || '').trim();
    if (!barcode || barcode.length < 6) {
      if (!silent) toast.error('Barkod en az 6 haneli olmalı');
      return;
    }
    setLookupLoading(true);
    try {
      const r = await fetch(`${API}/products/barcode-lookup/${encodeURIComponent(barcode)}`, { headers });
      if (!r.ok) throw new Error();
      const data = await r.json();
      if (!data.found) {
        setLookupSuggestion(null);
        if (!silent) toast.info('Bu barkod için ürün bulunamadı');
        return;
      }
      setLookupSuggestion(data);
    } catch {
      if (!silent) toast.error('Barkod sorgulama başarısız');
    } finally {
      setLookupLoading(false);
    }
  };

  const checkDuplicateBarcode = (barcode) => {
    if (!barcode || barcode.length < 4) { setDuplicateProduct(null); return; }
    const bc = barcode.trim();
    const found = products.find(p => (p.barcode || '').trim() === bc && (!editing || p.id !== editing.id));
    setDuplicateProduct(found || null);
  };

  const onBarcodeChange = (v) => {
    setForm(prev => ({ ...prev, barcode: v }));
    setLookupSuggestion(null);
    const val = (v || '').trim();
    checkDuplicateBarcode(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (editing) return;
    if (val.length < 8) { setAutoLookupTriedFor(''); return; }
    // Aynı barkodda zaten ürün varsa API'ye gitme
    const existsLocal = products.some(p => (p.barcode || '').trim() === val);
    if (existsLocal) { setAutoLookupTriedFor(val); return; }
    if (autoLookupTriedFor === val) return;
    debounceRef.current = setTimeout(() => {
      setAutoLookupTriedFor(val);
      runBarcodeLookup(val, { silent: true });
    }, 700);
  };

  const applySuggestion = () => {
    if (!lookupSuggestion) return;
    const s = lookupSuggestion;
    setForm(prev => ({
      ...prev,
      name: prev.name || s.name || prev.name,
      category: prev.category || s.category || prev.category,
      description: prev.description || s.description || prev.description,
      brand: prev.brand || s.brand || prev.brand,
      image_url: prev.image_url || s.image_url || prev.image_url,
      origin: prev.origin || s.origin || prev.origin,
    }));
    setLookupSuggestion(null);
    toast.success('Öneri uygulandı');
  };

  const filtered = useMemo(() => products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.barcode || '').includes(search) ||
    (p.category || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.supplier || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.brand || '').toLowerCase().includes(search.toLowerCase())
  ), [products, search]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="hesap-products-page">
      {/* İstatistik Kartları */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard icon={Package} label="Toplam Ürün" value={stats.total_products} color="sky" />
          <StatCard icon={Boxes} label="Toplam Stok" value={stats.total_stock} color="indigo" />
          <StatCard icon={Wallet} label="Alış Değeri" value={money(stats.total_buy_value)} color="amber" hidden={hidePrices} />
          <StatCard icon={TrendingUp} label="Satış Değeri" value={money(stats.total_sell_value)} color="emerald" />
          <StatCard
            icon={PackageX}
            label={stats.low_stock_count > 0 ? `Düşük/Bitmiş Stok` : 'Stok Uyarısı Yok'}
            value={`${stats.low_stock_count} / ${stats.zero_stock_count}`}
            subtitle={stats.low_stock_count > 0 ? 'düşük / stok yok' : 'her şey yolunda'}
            color="rose"
          />
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Ürünler ({products.length})</h1>
          <label className="flex items-center gap-1.5 text-xs bg-slate-100 dark:bg-slate-800 px-2.5 py-1.5 rounded cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hidePrices}
              onChange={(e) => setHidePrices(e.target.checked)}
              className="rounded"
              data-testid="hide-prices-toggle"
            />
            {hidePrices ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            Alış Fiyatlarını Gizle
          </label>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowCatDialog(true)}>Kategori Ekle</Button>
          <Button onClick={() => openDialog()} data-testid="add-product-btn">
            <Plus className="h-4 w-4 mr-1" />Yeni Ürün
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Ürün, barkod, marka, tedarikçi veya kategori ara..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="product-search"
        />
      </div>

      {filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500">{search ? 'Sonuç bulunamadı' : 'Henüz ürün yok'}</p>
        </CardContent></Card>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-lg border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800 border-b">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">Ürün</th>
                <th className="text-left p-3 font-medium text-slate-600">Barkod</th>
                <th className="text-left p-3 font-medium text-slate-600">Kategori</th>
                <th className="text-left p-3 font-medium text-slate-600">Tedarikçi</th>
                {!hidePrices && <th className="text-right p-3 font-medium text-slate-600">Alış</th>}
                <th className="text-right p-3 font-medium text-slate-600">Satış</th>
                <th className="text-right p-3 font-medium text-slate-600">Stok</th>
                <th className="text-right p-3 font-medium text-slate-600">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800">
                  <td className="p-3 font-medium">
                    <div className="flex items-center gap-2">
                      {p.image_url && (
                        <img src={p.image_url} alt="" className="h-8 w-8 object-cover rounded border" loading="lazy" />
                      )}
                      <div>
                        <div>{p.name}</div>
                        {p.brand && <div className="text-[10px] text-slate-400">{p.brand}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-slate-500">{p.barcode || '-'}</td>
                  <td className="p-3"><span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-xs">{p.category || '-'}</span></td>
                  <td className="p-3 text-slate-500 text-xs">{p.supplier || '-'}</td>
                  {!hidePrices && <td className="p-3 text-right">{(p.buy_price || 0).toLocaleString('tr-TR')} TL</td>}
                  <td className="p-3 text-right font-medium">{(p.sell_price || 0).toLocaleString('tr-TR')} TL</td>
                  <td className="p-3 text-right">
                    <span className={`inline-flex items-center gap-1 ${p.stock <= p.min_stock ? 'text-red-600 font-medium' : ''}`}>
                      {p.stock <= p.min_stock && <AlertTriangle className="h-3 w-3" />}
                      {p.stock} {p.unit}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog(p)}><Edit2 className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteProduct(p)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Datalists for autocomplete */}
      <datalist id="brand-list">
        {suggestions.brands.map(b => <option key={b} value={b} />)}
      </datalist>
      <datalist id="supplier-list">
        {suggestions.suppliers.map(s => <option key={s} value={s} />)}
      </datalist>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Ürünü Düzenle' : 'Yeni Ürün'}</DialogTitle></DialogHeader>
          <form onSubmit={saveProduct} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1">
                <Label>Ürün Adı *</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="product-name-input" />
              </div>
              <div className="col-span-2 space-y-1">
                <Label>Barkod</Label>
                <div className="flex gap-2">
                  <Input
                    value={form.barcode}
                    onChange={(e) => onBarcodeChange(e.target.value)}
                    placeholder="Barkodu yazın veya okutun..."
                    data-testid="product-barcode-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => runBarcodeLookup(form.barcode)}
                    disabled={lookupLoading || !form.barcode || form.barcode.length < 6}
                    data-testid="barcode-lookup-btn"
                  >
                    {lookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" />Ara</>}
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400">8+ haneli barkod yazınca otomatik aranır.</p>
              </div>

              {/* Duplicate barkod uyarısı */}
              {duplicateProduct && (
                <div className="col-span-2 border-2 border-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 flex items-start gap-2" data-testid="duplicate-warning">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold text-amber-900 dark:text-amber-200">Bu barkod zaten kayıtlı!</p>
                    <p className="text-slate-700 dark:text-slate-300">
                      <span className="font-medium">{duplicateProduct.name}</span>
                      {duplicateProduct.brand && ` — ${duplicateProduct.brand}`}
                      {' · '}Stok: {duplicateProduct.stock} {duplicateProduct.unit}
                    </p>
                    <button
                      type="button"
                      onClick={() => { setShowDialog(false); setTimeout(() => openDialog(duplicateProduct), 100); }}
                      className="text-xs text-amber-800 dark:text-amber-300 underline mt-1"
                    >
                      Mevcut ürünü düzenle →
                    </button>
                  </div>
                </div>
              )}

              {/* Öneri kartı */}
              {lookupSuggestion && (
                <div className="col-span-2 border-2 border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 space-y-2" data-testid="barcode-suggestion">
                  <div className="flex items-start gap-3">
                    {lookupSuggestion.image_url && (
                      <img src={lookupSuggestion.image_url} alt="" className="h-16 w-16 object-cover rounded border" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-emerald-900 dark:text-emerald-200 flex items-center gap-1">
                        <Sparkles className="h-4 w-4" /> Bu ürün mü?
                        {lookupSuggestion.source && <span className="text-[10px] font-normal ml-1">({lookupSuggestion.source})</span>}
                      </p>
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{lookupSuggestion.name}</p>
                      {lookupSuggestion.brand && <p className="text-xs text-slate-600 dark:text-slate-400">Marka: {lookupSuggestion.brand}</p>}
                      {lookupSuggestion.category && <p className="text-xs text-slate-600 dark:text-slate-400">Kategori: {lookupSuggestion.category}</p>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={applySuggestion} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="apply-suggestion-btn">
                      Alanları Doldur
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setLookupSuggestion(null)}>Hayır</Button>
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <Label>Kategori</Label>
                <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="">Seçiniz</option>
                  {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  {form.category && !categories.some(c => c.name === form.category) && (
                    <option value={form.category}>{form.category} (yeni)</option>
                  )}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Marka</Label>
                <Input
                  value={form.brand}
                  onChange={(e) => setForm({...form, brand: e.target.value})}
                  placeholder="Ör: Ülker"
                  list="brand-list"
                  data-testid="product-brand-input"
                />
              </div>
              <div className="col-span-2 space-y-1">
                <Label>Tedarikçi</Label>
                <Input
                  value={form.supplier}
                  onChange={(e) => setForm({...form, supplier: e.target.value})}
                  placeholder="Ör: ABC Ltd. Şti."
                  list="supplier-list"
                  data-testid="product-supplier-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Alış Fiyatı (TL)</Label>
                <Input type="number" step="0.01" value={form.buy_price} onChange={(e) => setForm({...form, buy_price: e.target.value})} />
              </div>
              <div className="space-y-1">
                <Label>Satış Fiyatı (TL)</Label>
                <Input type="number" step="0.01" value={form.sell_price} onChange={(e) => setForm({...form, sell_price: e.target.value})} />
              </div>
              <div className="space-y-1">
                <Label>Stok</Label>
                <Input type="number" value={form.stock} onChange={(e) => setForm({...form, stock: e.target.value})} />
              </div>
              <div className="space-y-1">
                <Label>Birim</Label>
                <select value={form.unit} onChange={(e) => setForm({...form, unit: e.target.value})}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="adet">Adet</option><option value="kg">Kg</option>
                  <option value="metre">Metre</option><option value="paket">Paket</option>
                  <option value="kutu">Kutu</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label>Min. Stok Uyarısı</Label>
                <Input type="number" value={form.min_stock} onChange={(e) => setForm({...form, min_stock: e.target.value})} />
              </div>
              <div className="space-y-1">
                <Label>Menşei</Label>
                <Input value={form.origin} onChange={(e) => setForm({...form, origin: e.target.value})} placeholder="Ör: Türkiye" />
              </div>
              <div className="col-span-2 space-y-1">
                <Label>Açıklama</Label>
                <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[60px]" />
              </div>
              {form.image_url && (
                <div className="col-span-2 flex items-center gap-3 p-2 bg-slate-50 dark:bg-slate-800 rounded">
                  <img src={form.image_url} alt="" className="h-14 w-14 object-cover rounded border" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-500 truncate">Ürün görseli</p>
                    <p className="text-[10px] text-slate-400 truncate">{form.image_url}</p>
                  </div>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setForm({...form, image_url: ''})}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1">İptal</Button>
              <Button type="submit" disabled={saving} className="flex-1">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showCatDialog} onOpenChange={setShowCatDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Yeni Kategori</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={catForm} onChange={(e) => setCatForm(e.target.value)} placeholder="Kategori adı" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowCatDialog(false)} className="flex-1">İptal</Button>
              <Button onClick={addCategory} className="flex-1">Ekle</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, subtitle, color = 'sky', hidden = false }) {
  const colors = {
    sky: 'from-sky-500 to-sky-600 text-white',
    indigo: 'from-indigo-500 to-indigo-600 text-white',
    amber: 'from-amber-500 to-amber-600 text-white',
    emerald: 'from-emerald-500 to-emerald-600 text-white',
    rose: 'from-rose-500 to-rose-600 text-white',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-lg p-3 shadow-sm`}>
      <div className="flex items-center justify-between">
        <p className="text-xs opacity-90">{label}</p>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <p className="text-xl font-bold mt-1 truncate">{hidden ? '••••' : value}</p>
      {subtitle && <p className="text-[10px] opacity-80 mt-0.5">{subtitle}</p>}
    </div>
  );
}
